/**
 * Pin Style Presets for Map Notes
 * Clean solid-color pins with single-character markers.
 * Each preset is visually distinct by color + letter combo.
 * Deadline ring thresholds for time-based urgency visualization.
 */

export const PIN_PRESETS = [
  // === EMERGENCY (animated) ===
  { key: 'police_lights', label: 'Emergency', group: 'emergency', animated: true,
    color: '#D50000', letter: 'E', pattern: 'alternate', altColor: '#1565C0', speed: '0.6s' },
  { key: 'amber_flash', label: 'Urgent', group: 'emergency', animated: true,
    color: '#FF6F00', letter: 'U', pattern: 'strobe', speed: '0.8s' },
  { key: 'priority_rush', label: 'Priority', group: 'emergency', animated: true,
    color: '#D50000', letter: '!', pattern: 'pulse_scale', speed: '0.5s' },

  // === HAZARD ===
  { key: 'construction', label: 'Construction', group: 'hazard', animated: false,
    color: '#E65100', letter: 'C' },
  { key: 'caution', label: 'Caution', group: 'hazard', animated: false,
    color: '#F9A825', letter: '!' },
  { key: 'hazard_tape', label: 'Danger', group: 'hazard', animated: false,
    color: '#C62828', letter: 'D' },
  { key: 'safety_concern', label: 'Safety', group: 'hazard', animated: false,
    color: '#AEEA00', letter: 'S', textColor: '#33691E' },

  // === SITUATIONAL ===
  { key: 'utility_conflict', label: 'Utility', group: 'situational', animated: true,
    color: '#F57F17', letter: 'U', pattern: 'glow_pulse', speed: '1.5s' },
  { key: 'road_closure', label: 'Road Closed', group: 'situational', animated: false,
    color: '#B71C1C', letter: 'X' },
  { key: 'blocked', label: 'Blocked', group: 'situational', animated: false,
    color: '#880E4F', letter: 'B' },
  { key: 'damage', label: 'Damage', group: 'situational', animated: false,
    color: '#D32F2F', letter: 'D' },
  { key: 'fiber_break', label: 'Fiber Break', group: 'situational', animated: false,
    color: '#B71C1C', letter: 'F' },
  { key: 'weather_hold', label: 'Weather', group: 'situational', animated: false,
    color: '#546E7A', letter: 'W' },
  { key: 'permit_required', label: 'Permit', group: 'situational', animated: false,
    color: '#6A1B9A', letter: 'P' },

  // === OPERATIONAL ===
  { key: 'inspection_ready', label: 'Inspect', group: 'operational', animated: true,
    color: '#0D47A1', letter: 'I', pattern: 'glow_pulse', speed: '2s' },
  { key: 'hot_zone', label: 'Hot Zone', group: 'operational', animated: true,
    color: '#BF360C', letter: 'H', pattern: 'radiate', speed: '1.5s' },
  { key: 'restoration', label: 'Restore', group: 'operational', animated: false,
    color: '#4E342E', letter: 'R' },
  { key: 'night_work', label: 'Night', group: 'operational', animated: false,
    color: '#1A237E', letter: 'N' },
  { key: 'client_visit', label: 'Client', group: 'operational', animated: false,
    color: '#FF8F00', letter: 'V' },
  { key: 'completed_verified', label: 'Done', group: 'operational', animated: false,
    color: '#2E7D32', letter: 'OK', textColor: '#2E7D32' },
];

export const PIN_STYLE_GROUPS = [
  { key: 'emergency', label: 'Emergency' },
  { key: 'hazard', label: 'Hazard' },
  { key: 'situational', label: 'Situational' },
  { key: 'operational', label: 'Operational' },
];

export function getPreset(key) {
  return PIN_PRESETS.find(p => p.key === key) || null;
}

export function isCustomStyle(style) {
  return style && style.startsWith('custom:');
}

export function getCustomColor(style) {
  if (!isCustomStyle(style)) return null;
  return style.replace('custom:', '');
}

// Deadline ring thresholds — minutes remaining → visual ring
export const DEADLINE_RINGS = [
  { threshold: 24 * 60, color: null },
  { threshold: 12 * 60, color: '#2196F3', width: 2 },
  { threshold: 4 * 60,  color: '#FF9800', width: 2.5 },
  { threshold: 60,      color: '#E65100', width: 3, pulse: 'slow' },
  { threshold: 0,       color: '#F44336', width: 3.5, pulse: 'fast' },
  { threshold: -Infinity, color: '#D32F2F', width: 4, pulse: 'flash' },
];

export function getDeadlineRing(deadline) {
  if (!deadline) return null;
  const minsLeft = (new Date(deadline) - Date.now()) / 60000;
  for (const ring of DEADLINE_RINGS) {
    if (minsLeft > ring.threshold) return ring;
  }
  return DEADLINE_RINGS[DEADLINE_RINGS.length - 1];
}

export function getAllKeyframes() {
  return `
    @keyframes lyt-police { 0%,49%{fill:#D50000} 50%,100%{fill:#1565C0} }
    @keyframes lyt-strobe { 0%,70%{opacity:1} 71%,100%{opacity:0.3} }
    @keyframes lyt-pulse-scale { 0%{transform:scale(1)} 50%{transform:scale(1.15)} 100%{transform:scale(1)} }
    @keyframes lyt-glow { 0%{filter:drop-shadow(0 0 2px currentColor)} 50%{filter:drop-shadow(0 0 8px currentColor)} 100%{filter:drop-shadow(0 0 2px currentColor)} }
    @keyframes lyt-radiate { 0%{r:14;opacity:0.6} 100%{r:20;opacity:0} }
    @keyframes lyt-deadline-slow { 0%{opacity:1} 50%{opacity:0.4} 100%{opacity:1} }
    @keyframes lyt-deadline-fast { 0%{opacity:1} 50%{opacity:0.2} 100%{opacity:1} }
    @keyframes lyt-deadline-flash { 0%{opacity:1} 30%{opacity:0} 60%{opacity:1} 100%{opacity:1} }
  `;
}

// Build a clean solid-color pin SVG data URI
export function buildPresetPinSvg(presetKey, category, deadline) {
  const preset = getPreset(presetKey);
  const customColor = isCustomStyle(presetKey) ? getCustomColor(presetKey) : null;

  let bodyColor, letter, textColor;

  if (customColor) {
    bodyColor = customColor;
    letter = { general: 'G', issue: '!', safety: 'S', question: '?', instruction: 'I' }[category] || 'G';
    textColor = customColor;
  } else if (!preset) {
    return null;
  } else {
    bodyColor = preset.color;
    letter = preset.letter;
    textColor = preset.textColor || preset.color;
  }

  // Deadline ring
  let deadlineRing = '';
  const ring = getDeadlineRing(deadline);
  if (ring && ring.color) {
    deadlineRing = `<circle cx='16' cy='16' r='15' fill='none' stroke='${ring.color}' stroke-width='${ring.width || 2}' opacity='0.85'/>`;
  }

  const fontSize = letter.length > 1 ? 9 : 12;

  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='40'>
    ${deadlineRing}
    <path d='M16 40 C16 40 0 24 0 16 C0 7.16 7.16 0 16 0 C24.84 0 32 7.16 32 16 C32 24 16 40 16 40Z' fill='${bodyColor}' stroke='white' stroke-width='2'/>
    <circle cx='16' cy='15' r='9' fill='white' opacity='0.9'/>
    <text x='16' y='${letter.length > 1 ? 18 : 19}' text-anchor='middle' font-size='${fontSize}' font-weight='700' font-family='Arial' fill='${textColor}'>${letter}</text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// Build inline SVG HTML for animated OverlayView pins
export function buildAnimatedPinHtml(preset, category, deadline, noteId) {
  if (!preset) return null;
  const letter = preset.letter;
  const textColor = preset.textColor || preset.color;
  const fontSize = letter.length > 1 ? 9 : 12;

  let bodyFill = preset.color;
  let animation = '';
  let outerWrap = '';
  let extraElements = '';

  if (preset.pattern === 'alternate') {
    animation = `animation: lyt-police ${preset.speed} step-end infinite;`;
  } else if (preset.pattern === 'strobe') {
    animation = `animation: lyt-strobe ${preset.speed} ease-in-out infinite;`;
  } else if (preset.pattern === 'pulse_scale') {
    outerWrap = `animation: lyt-pulse-scale ${preset.speed} ease-in-out infinite;`;
  } else if (preset.pattern === 'glow_pulse') {
    animation = `animation: lyt-glow ${preset.speed} ease-in-out infinite; color: ${preset.color};`;
  } else if (preset.pattern === 'radiate') {
    extraElements = `<circle cx='16' cy='16' r='14' fill='none' stroke='${preset.color}' stroke-width='2' opacity='0.6'>
      <animate attributeName='r' from='14' to='22' dur='${preset.speed}' repeatCount='indefinite'/>
      <animate attributeName='opacity' from='0.6' to='0' dur='${preset.speed}' repeatCount='indefinite'/>
    </circle>`;
  }

  // Deadline ring
  let deadlineRing = '';
  const ring = getDeadlineRing(deadline);
  if (ring && ring.color) {
    const pulseAnim = ring.pulse ? `animation: lyt-deadline-${ring.pulse} ${ring.pulse === 'flash' ? '0.8s' : ring.pulse === 'fast' ? '1s' : '2s'} ease-in-out infinite;` : '';
    deadlineRing = `<circle cx='16' cy='16' r='15' fill='none' stroke='${ring.color}' stroke-width='${ring.width || 2}' opacity='0.85' style='${pulseAnim}'/>`;
  }

  const fillStyle = preset.pattern === 'alternate' ? `style='${animation}'` : '';
  const svgStyle = animation && preset.pattern !== 'alternate' ? `style='${animation}'` : '';
  const wrapStyle = outerWrap ? `style='${outerWrap} display:inline-block;'` : '';

  return `<div ${wrapStyle}><svg xmlns='http://www.w3.org/2000/svg' width='32' height='40' ${svgStyle}>
    ${deadlineRing}${extraElements}
    <path d='M16 40 C16 40 0 24 0 16 C0 7.16 7.16 0 16 0 C24.84 0 32 7.16 32 16 C32 24 16 40 16 40Z' fill='${bodyFill}' stroke='white' stroke-width='2' ${fillStyle}/>
    <circle cx='16' cy='15' r='9' fill='white' opacity='0.9'/>
    <text x='16' y='${letter.length > 1 ? 18 : 19}' text-anchor='middle' font-size='${fontSize}' font-weight='700' font-family='Arial' fill='${textColor}'>${letter}</text>
  </svg></div>`;
}
